import * as React from "react";

import { BookingWindow } from "@/api/types";

import { AvailabilityCalendar } from "../AvailabilityCalendar";
import { AvailabilityCanvas } from "../AvailabilityCanvas";

/**
 * The poll's time picker: the shared drag calendar in {@code clickToPlace} mode, with the proposed
 * times listed beside it.
 *
 * <p>A poll does not draw availability, it PROPOSES times, so the interaction is deliberately
 * smaller than the other two builders: a click puts one time down, a click on one takes it away,
 * and every time is the same length. There is no fill-from-a-availability-pattern here either — a poll asks
 * a specific group about a handful of specific times, and filling a week of them from a pattern is
 * the opposite of the point.</p>
 *
 * <p>The wrapper exists for the one thing the calendar cannot do itself: when the host changes the
 * duration, every already-placed time has to be re-stamped to the new length.</p>
 */
export function PollAvailabilityCalendar({
  windows,
  onChange,
  durationMinutes,
  showMine,
  headerLeft,
  headerCenter,
  headerAbove,
  topNotice,
}: {
  windows: BookingWindow[];
  onChange: (next: BookingWindow[]) => void;
  /** One length for every proposed time. */
  durationMinutes: number;
  showMine: boolean;
  headerLeft?: React.ReactNode;
  headerCenter?: React.ReactNode;
  headerAbove?: React.ReactNode;
  topNotice?: React.ReactNode;
}) {
  return (
    <section className="space-y-3">
      <div className="grid gap-3 lg:grid-cols-[3fr_1fr] lg:items-start">
        <div className="min-w-0">
          <AvailabilityCalendar
            windows={windows}
            onChange={onChange}
            // Required by the shared component but unread since the pointer layer replaced
            // month-view drag-create; the rolling calendar passes the same placeholders.
            defaultFrom="09:00"
            defaultTo="17:00"
            showMine={showMine}
            slotMinutes={durationMinutes}
            clickToPlace
            headerLeft={headerLeft}
            headerCenter={headerCenter}
            headerAbove={headerAbove}
            topNotice={topNotice}
          />
        </div>
        <AvailabilityCanvas
          windows={windows}
          onRemove={(i) => onChange(windows.filter((_, idx) => idx !== i))}
          title="Proposed times"
          emptyText="No times yet. Click the calendar to propose one."
        />
      </div>
    </section>
  );
}

/**
 * Re-stamp every placed time to a new length, keeping its start. Returned rather than applied so
 * the caller can offer an Undo: changing the duration silently rewrites work the host already did.
 */
export function restampWindows(windows: BookingWindow[], durationMinutes: number): BookingWindow[] {
  return windows.map((w) => {
    const start = new Date(w.start);
    return { start: w.start, end: new Date(start.getTime() + durationMinutes * 60_000).toISOString() };
  });
}
